import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { textStyle } from "../../styles/Style";
import {
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  reserveLocationFieldErrors,
  reserveLocationFieldHelperText,
} from "../../../constants/reserveLocationValidationHelperText";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  reserveLocationTooltip,
  reserveListAvailableHeadingTooltip,
  reserveRowFieldTooltip,
} from "../../../constants/ReserveTooltipInformation";
import "../../styles/popupStyle.css";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";
import useScrollToTop from "../../hooks/useScrollToTop";

const ReserveLocation = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [area, setArea] = useState<string | null>(null);
  const [locationType, setLocationType] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [areaError, setAreaError] = useState<boolean>(false);
  const [locationTypeError, setLocationTypeError] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [locations, setLocations] = useState<any[]>([]);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const validateInput = () => {
    setAreaError(false);
    setLocationTypeError(false);

    let ok = true;
    const textMax = validationLength.roomFeatureName;
    const areaTrim = (area ?? "").trim();
    const locationTypeTrim = (locationType ?? "").trim();

    if (!areaTrim) {
      setAreaError(true);
      ok = false;
    } else if (!validateString(areaTrim, textMax, validationType.SPACE)) {
      setAreaError(true);
      ok = false;
    }

    if (!locationTypeTrim) {
      setLocationTypeError(true);
      ok = false;
    } else if (
      !validateString(locationTypeTrim, textMax, validationType.SPACE)
    ) {
      setLocationTypeError(true);
      ok = false;
    }

    if (!ok) {
      if (!areaTrim) {
        setAlertMessage("Area is mandatory");
      } else if (!locationTypeTrim) {
        setAlertMessage("Location type is mandatory");
      } else {
        setAlertMessage("Please correct the highlighted fields");
      }
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    setLocations(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(locations);
    editClicked &&
      setSelectedId(checkData(locations, selectedId) ? selectedId : null);
  }, [locations]);

  const updateLocation = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(
        (locations ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        "Area",
        area,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setAreaError(false);
    setLocationTypeError(false);
    setLocations((prev: any) => {
      return (prev ?? []).map((item: any) => {
        if (String(item.id) === String(selectedId)) {
          return {
            ...item,
            ...payload,
          };
        }
        return item;
      });
    });
    editLocation(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${ReservePropertyEnum.location} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addLocation = () => {
    if (!validateInput()) {
      return;
    }
    if (checkDuplicate(locations ?? [], "Area", area)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setLocations((prev: any) => {
      return updateData(
        Array.isArray(prev) && prev.length
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      );
    });
    setLastUpdatedRecord(payload);
    editLocation(null);
    setEditClicked(false);
    setSelectedId(null);
  };

  const editLocation = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    if (list == null) {
      setArea(null);
      setLocationType(null);
      setActive(true);
      setAreaError(false);
      setLocationTypeError(false);
      setEditClicked(false);
      return;
    }
    setArea(list?.Area ?? null);
    setLocationType(list?.["Location Type"] ?? null);
    setActive(list?.Active ?? true);
    setAreaError(false);
    setLocationTypeError(false);
  };

  const deleteLocation = (id: number) => {
    setLocations(deleteItem(locations ?? [], id));
  };

  const payload = {
    Area: area,
    "Location Type": locationType,
    Active: active,
  };

  const areaHelperText = areaError
    ? !(area?.trim() ?? "")
      ? reserveLocationFieldErrors.required
      : reserveLocationFieldErrors.area
    : reserveLocationFieldHelperText.area;

  const locationTypeHelperText = locationTypeError
    ? !(locationType?.trim() ?? "")
      ? reserveLocationFieldErrors.required
      : reserveLocationFieldErrors.locationType
    : reserveLocationFieldHelperText.locationType;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              {ReservePropertyEnum.location}
              <Tooltip title={tooltipComponent(reserveLocationTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editLocation(null);
                    setSelectedId(null);
                    setEditClicked(false);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editLocation(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Area
              <Tooltip title={tooltipComponent(reserveLocationTooltip.area)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="reserveLocationArea"
              variant="outlined"
              hiddenLabel
              placeholder="Area"
              value={area ?? ""}
              helperText={areaHelperText}
              error={areaError}
              onChange={(e: any) => {
                setArea(e.target.value);
                setAreaError(false);
              }}
              fullWidth
              inputProps={{ "aria-label": "Area" }}
            />
          </Stack>

          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Location Type
              <Tooltip
                title={tooltipComponent(reserveLocationTooltip.locationType)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="reserveLocationType"
              variant="outlined"
              hiddenLabel
              placeholder="Location Type"
              value={locationType ?? ""}
              helperText={locationTypeHelperText}
              error={locationTypeError}
              onChange={(e: any) => {
                setLocationType(e.target.value);
                setLocationTypeError(false);
              }}
              fullWidth
              inputProps={{ "aria-label": "Location Type" }}
            />
          </Stack>

          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Active?
              <Tooltip title={tooltipComponent(reserveRowFieldTooltip.active)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Stack
              className="form-checkbox-row form-two-col-row"
              direction="row"
              alignItems="center"
              gap={1}
            >
              <Switch
                checked={active}
                onChange={() => setActive(!active)}
                inputProps={{ "aria-label": "Active location" }}
              />
              <Typography variant="body2" color="text.secondary">
                {active ? "Yes" : "No"}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() => (selectedId ? updateLocation() : addLocation())}
              id="addReserveLocation"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}{" "}
              {ReservePropertyEnum.location}
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography
              variant="h6"
              component="div"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {ReservePropertyEnum.location} available
              <Tooltip
                title={tooltipComponent(reserveListAvailableHeadingTooltip)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="column" gap="10px">
              {Array.isArray(locations) && locations.length > 0 ? (
                <DraggableItemList
                  itemData={locations}
                  onOrderUpdate={(order) => setLocations(order)}
                  onEdit={(info) => {
                    editLocation(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteLocation(id)}
                  code="Area"
                  name="Location Type"
                />
              ) : (
                <Typography variant="body2">
                  No {ReservePropertyEnum.location} information available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default ReserveLocation;
